import type { CollectionSummary } from "../../../utils/pensionCollection";

export interface NextStepInput {
  collectionState: CollectionSummary["state"];
  isChatAvailable: boolean;
  advisoryRequestSent: boolean;
}

export interface NextStepDecision {
  /** Show the advisory card in the carousel. */
  advisory: boolean;
  /** Show the Sam card as a way to keep learning. */
  sam: boolean;
  /** Short machine readable reason, logged with the CTA impression. */
  reason: string;
}

/**
 * PEN-169: which next step the customer is offered from the POA pension overview.
 *
 * The Insurely flow only offers an advisor when the backend found a fee saving, and
 * shows Sam to everyone else (see CalculationScreen). Here the advisor is offered to
 * every POA customer, while the data is still being collected as well as after, since
 * a customer who has just handed over a POA is at their most engaged.
 *
 * Sam is offered alongside the advisor rather than as a fallback, so nobody reaches a
 * dead end. PEN-169 reads as though Sam goes away once an income is added; product
 * decided against that, and this is the behaviour they asked for.
 *
 * The criteria live in this one function so narrowing them later is a single change,
 * and so the reason travels with the tracking event.
 */
export function decideNextStep({
  collectionState,
  isChatAvailable,
  advisoryRequestSent,
}: NextStepInput): NextStepDecision {
  const sam = isChatAvailable;

  // The design keeps the card up until the customer actually submits the form, and
  // takes it away afterwards rather than repeating the offer. Sam stays, so the
  // overview is not a dead end.
  if (advisoryRequestSent) {
    return { advisory: false, sam, reason: "ALREADY_REQUESTED" };
  }

  return {
    advisory: true,
    sam,
    reason:
      collectionState === "COMPLETE"
        ? "ALL_USERS_COMPLETE"
        : "ALL_USERS_COLLECTING",
  };
}
